import { useState } from "react";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import {
  useTransportRates,
  useCreateTransportRate,
  useUpdateTransportRate,
  useDeleteTransportRate,
} from "../hooks/useTransportRates";
import type { TransportRateDto } from "../services/transportRatesApi";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import { useDebounce } from "@/shared/hooks/useDebounce";

// ── Helpers ───────────────────────────────────────────────────────────────

const formatCost = (amount: number) =>
  `KES ${amount.toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Create Dialog ─────────────────────────────────────────────────────────

const EMPTY_CREATE = {
  destinationName: "",
  region: "",
  deliveryAreas: "",
  costAmount: "",
  notes: "",
};

const CreateTransportRateDialog = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CREATE);
  const createMutation = useCreateTransportRate();
  const confirm = useConfirm();

  const set = (field: keyof typeof EMPTY_CREATE, value: string) =>
    setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cost = parseFloat(form.costAmount);
    if (isNaN(cost) || cost < 0) return;
    confirm({
      title: "Create transport rate?",
      description: `A rate of ${formatCost(cost)} per tray for “${form.destinationName.trim()}” will be added.`,
      confirmLabel: "Create rate",
      onConfirm: () =>
        withConfirmSuccess(
          createMutation.mutateAsync({
            destinationName: form.destinationName.trim(),
            region: form.region.trim() || undefined,
            deliveryAreas: form.deliveryAreas.split(",").map((area) => area.trim()).filter(Boolean),
            costAmount: cost,
            notes: form.notes.trim() || undefined,
          }),
          "Transport rate created"
        ).then(() => {
          setForm(EMPTY_CREATE);
          setOpen(false);
        }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Add Transport Rate
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Transport Rate</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <Field>
            <FieldLabel>Pricing region *</FieldLabel>
            <Input
              placeholder="e.g. Nairobi"
              value={form.destinationName}
              onChange={(e) => set("destinationName", e.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel>Reporting group (optional)</FieldLabel>
            <Input
              placeholder="e.g. Central Kenya"
              value={form.region}
              onChange={(e) => set("region", e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel>Rate per Tray (KES) *</FieldLabel>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={form.costAmount}
              onChange={(e) => set("costAmount", e.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel>Delivery areas *</FieldLabel>
            <Input value={form.deliveryAreas} onChange={(e) => set("deliveryAreas", e.target.value)}
              placeholder="Westlands, Kasarani, Karen" required />
            <p className="text-xs text-muted-foreground">Separate area names with commas. All use this region's rate.</p>
          </Field>
          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Input
              placeholder="Optional notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Saving…" : "Create Rate"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Edit Dialog ───────────────────────────────────────────────────────────

const EditTransportRateDialog = ({
  rate,
  open,
  onOpenChange,
}: {
  rate: TransportRateDto;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const [form, setForm] = useState({
    destinationName: rate.destinationName,
    region: rate.region ?? "",
    deliveryAreas: (rate.deliveryAreas ?? []).map((area) => area.name).join(", "),
    costAmount: String(rate.costAmount),
    notes: rate.notes ?? "",
    isActive: rate.isActive,
  });
  const updateMutation = useUpdateTransportRate();
  const confirm = useConfirm();

  const set = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cost = parseFloat(form.costAmount);
    if (isNaN(cost) || cost < 0) return;
    confirm({
      title: "Save transport rate changes?",
      description: `Changes to the rate for “${form.destinationName.trim()}” will be saved.`,
      confirmLabel: "Save changes",
      onConfirm: () =>
        withConfirmSuccess(
          updateMutation.mutateAsync({
            id: rate.id,
            data: {
              id: rate.id,
              destinationName: form.destinationName.trim(),
              region: form.region.trim() || undefined,
              deliveryAreas: form.deliveryAreas.split(",").map((area) => area.trim()).filter(Boolean),
              costAmount: cost,
              notes: form.notes.trim() || undefined,
              isActive: form.isActive,
            },
          }),
          "Transport rate updated"
        ).then(() => onOpenChange(false)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Transport Rate</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <Field>
            <FieldLabel>Pricing region *</FieldLabel>
            <Input
              value={form.destinationName}
              onChange={(e) => set("destinationName", e.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel>Reporting group (optional)</FieldLabel>
            <Input
              value={form.region}
              onChange={(e) => set("region", e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel>Rate per Tray (KES) *</FieldLabel>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.costAmount}
              onChange={(e) => set("costAmount", e.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel>Delivery areas *</FieldLabel>
            <Input value={form.deliveryAreas} onChange={(e) => set("deliveryAreas", e.target.value)}
              placeholder="Westlands, Kasarani, Karen" required />
          </Field>
          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Input
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </Field>
          <div className="flex items-center gap-3">
            <Checkbox
              id="isActive"
              checked={form.isActive}
              onCheckedChange={(v) => set("isActive", v === true)}
            />
            <Label htmlFor="isActive">Active</Label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Rates Table ───────────────────────────────────────────────────────────

const TransportRatesTable = ({ rates, search, onSearchChange }: { rates: TransportRateDto[]; search: string; onSearchChange: (value: string) => void }) => {
  const deleteMutation = useDeleteTransportRate();
  const confirm = useConfirm();
  const [editTarget, setEditTarget] = useState<TransportRateDto | null>(null);

  const handleDelete = (id: string, name: string) => {
    confirm({
      title: "Remove transport rate?",
      description: `The rate for “${name}” will be removed. This cannot be undone.`,
      variant: "destructive",
      confirmLabel: "Remove",
      onConfirm: () =>
        withConfirmSuccess(
          deleteMutation.mutateAsync(id),
          `Transport rate for ${name} removed`
        ),
    });
  };

  return (
    <div className="space-y-4">
      <div className="relative w-64 ml-auto">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search destinations…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Pricing region</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Delivery areas</TableHead>
              <TableHead className="text-right">Rate per Tray</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rates.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                  No transport rates found.
                </TableCell>
              </TableRow>
            )}
            {rates.map((rate) => (
              <TableRow key={rate.id}>
                <TableCell className="font-medium">{rate.destinationName}</TableCell>
                <TableCell>{rate.region ?? "—"}</TableCell>
                <TableCell>{(rate.deliveryAreas ?? []).map((area) => area.name).join(", ") || "—"}</TableCell>
                <TableCell className="text-right font-mono">
                  {formatCost(rate.costAmount)}
                </TableCell>
                <TableCell className="text-gray-500 max-w-xs truncate">
                  {rate.notes ?? "—"}
                </TableCell>
                <TableCell>
                  <Badge variant={rate.isActive ? "default" : "secondary"}>
                    {rate.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditTarget(rate)}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDelete(rate.id, rate.destinationName)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editTarget && (
        <EditTransportRateDialog
          rate={editTarget}
          open={!!editTarget}
          onOpenChange={(v) => {
            if (!v) setEditTarget(null);
          }}
        />
      )}
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────

export const TransportRatesPage = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading, error, isFetching } = useTransportRates(pageNumber, debouncedSearch);

  if (isLoading) return <LoadingAnimation fullScreen />;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load transport rates.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <MapPin className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">Transport Rates</h1>
          </div>
          <p className="text-gray-500 mt-1">
            Configure delivery destination costs applied to customer orders.
          </p>
        </div>
        <CreateTransportRateDialog />
      </div>

      <TransportRatesTable rates={data?.items ?? []} search={search}
        onSearchChange={(value) => { setSearch(value); setPageNumber(1); }} />
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{isFetching ? "Updating…" : `${data?.totalCount ?? 0} rates`}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={pageNumber <= 1 || isFetching}
            onClick={() => setPageNumber((page) => page - 1)}>Previous</Button>
          <span className="self-center">Page {pageNumber} of {data?.totalPages || 1}</span>
          <Button variant="outline" size="sm" disabled={pageNumber >= (data?.totalPages || 1) || isFetching}
            onClick={() => setPageNumber((page) => page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
};
